import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CommunityStats.css';

function CommunityStats({ variant = 'banner' }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Show all stats — live mode
  const DEV_MODE = false;

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get('/api/stats/community');
      // Validate response has required structure
      if (response.data && response.data.display) {
        setStats(response.data);
      } else {
        console.warn('Stats API returned invalid data structure');
        setStats(null);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) return null;

  // Safety check: ensure stats has display property
  if (!stats.display) {
    console.error('Stats missing display property:', stats);
    return null;
  }

  // Hide community growth widget until we reach 10,000 members
  if (stats.totalUsers < 10000) return null;

  const { display } = stats;

  // In DEV MODE, always show the component
  // In production, don't show anything in launch phase with low numbers
  if (!DEV_MODE && display.phase === 'launch' && !display.showUsersThisWeek) {
    return null;
  }

  // Banner variant for login/register pages
  if (variant === 'banner') {
    return (
      <div className="community-stats-banner">
        <div className="stats-content">
          {display.showTotalUsers && (
            <span className="stat-item">
              <strong>{display.totalUsersDisplay}</strong> members
            </span>
          )}
          {display.showUsersThisWeek && !display.showTotalUsers && (
            <span className="stat-item">
              <strong>{stats.usersThisWeek}</strong> joined this week
            </span>
          )}
          {display.showConnectionsThisWeek && (
            <span className="stat-item">
              <strong>{stats.connectionsThisWeek}</strong> connections this week
            </span>
          )}
          {display.showEvents && (
            <span className="stat-item">
              <strong>{stats.upcomingEvents}</strong> upcoming events
            </span>
          )}
        </div>
      </div>
    );
  }

  // Card variant for dashboard
  if (variant === 'card') {
    return (
      <div className="community-stats-card">
        <h3>🌟 Community Growth</h3>
        <div className="stats-grid">
          {display.showTotalUsers && (
            <div className="stat-box">
              <div className="stat-number">{display.totalUsersDisplay}</div>
              <div className="stat-label">Members</div>
            </div>
          )}
          {display.showTotalConnections && (
            <div className="stat-box">
              <div className="stat-number">{stats.totalConnections}</div>
              <div className="stat-label">Connections</div>
            </div>
          )}
          {display.showGroups && (
            <div className="stat-box">
              <div className="stat-number">{stats.totalGroups}</div>
              <div className="stat-label">Groups</div>
            </div>
          )}
          {display.showEvents && (
            <div className="stat-box">
              <div className="stat-number">{stats.upcomingEvents}</div>
              <div className="stat-label">Events</div>
            </div>
          )}
        </div>

        {display.showCityBreakdown && stats.topCities.length > 0 && (
          <div className="top-cities">
            <h4>Top Cities</h4>
            <ul>
              {stats.topCities.slice(0, 3).map((city, idx) => (
                <li key={idx}>
                  📍 {city.current_city}, {city.current_state} ({city.user_count} members)
                </li>
              ))}
            </ul>
          </div>
        )}

        {display.phase === 'launch' && (
          <p className="launch-message">
            🚀 Join our growing community!
          </p>
        )}
      </div>
    );
  }

  return null;
}

export default CommunityStats;